/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Copyright (c) 2024, Ouster, Inc.
 * All rights reserved.
 * The store here has duplicated functions with tags store.
 * tags store still preserve the original ideas of tag category which we try to desperate
 * the tags store should be delete after we fully remove the concept of tag category
 */
import * as Sentry from '@sentry/react';
import {
  action,
  Action as EzPzAction,
  Thunk as EzPzThunk,
  thunk,
} from 'easy-peasy';
import { orderBy } from 'lodash';

import { Tag } from '../types';
import { BASE_ORG_API } from '../utils/constants';
import { formatTag } from '../utils/misc';
import { fetchWithAuthV2 } from '../utils/requests';

/// ///////////////////////////////////////////////////////////////////////////
// State Model
/// ///////////////////////////////////////////////////////////////////////////

export type State = {
  tags: Tag[];
};

export const defaultState: State = {
  tags: [],
};

export interface RecordingTagModel extends State {
  setTags: EzPzAction<RecordingTagModel, Tag[]>;
  fetchTags: EzPzThunk<RecordingTagModel, number>;
  createTag: EzPzThunk<
    RecordingTagModel,
    {
      organization_id: number;
      name: string;
    }
  >;
}

export const model: RecordingTagModel = {
  ...defaultState,
  setTags: action((state, tags) => {
    state.tags = orderBy(tags, 'name');
  }),
  fetchTags: thunk(async (actions, organization_id) => {
    const path = `${BASE_ORG_API}/${organization_id}/tags/`;

    try {
      const result = await fetchWithAuthV2(path, {
        fallbackErrorMessage: 'Failed to fetch tags',
      });
      actions.setTags(result as Tag[]);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      Sentry.captureException(err, {
        tags: { section: 'RecordingTagModel', thunk: 'fetchTags' },
        extra: { organization_id },
      });
    }
  }),
  createTag: thunk(async (actions, { organization_id, name }, { getState }) => {
    const path = `${BASE_ORG_API}/${organization_id}/tags/`;
    try {
      const resp = await fetchWithAuthV2(path, {
        method: 'POST',
        fallbackErrorMessage: 'Failed to create tag',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: formatTag(name) }),
      });
      const data = resp as Tag;
      const orgTags = getState().tags || [];
      actions.setTags([...orgTags, data]);
      return data;
    } catch (err) {
      Sentry.captureException(err, {
        tags: { section: 'RecordingTagModel', thunk: 'createTag' },
        extra: { organization_id, name },
      });
      if (err instanceof Error) {
        throw new Error(`Failed to create new tag: ${err.message}`);
      } else {
        throw new Error(`Failed to create new tag: Unknown error occurred`);
      }
    }
  }),
};
